import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';

import {
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from 'utils/deliverables';

import './task-selector.scss';

import type { DeliverableType } from 'types';
import type { TaskSelectorItem } from './types';

applyTranslations(require('./task-selector.i18n.json'));

interface Props {
  items: Array<TaskSelectorItem>,
  selectedTypes: Array<DeliverableType>,
  tooltipMsg: string,
  withIcon?: boolean,
  withSelectAll?: boolean,
  onChange: (items: Array<TaskSelectorItem>, identifier: string) => void
}

const SORT_TEMPLATE = [
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  DELIVERABLE_TYPE_OTHER
];

const MSG_KEY = 'task_selector';

const TaskSelector: React.FC<Props> = ({
  items,
  selectedTypes,
  tooltipMsg,
  withIcon,
  withSelectAll,
  onChange
}: Props) => {
  const [storedItems, setStoredItems] = React.useState([] as Array<TaskSelectorItem>);
  const areAllItemsEmpty = isEmpty(items);

  const selectableTypes = items
    .filter(item => item.selectable)
    .map(item => item.identifier);

  const areAllItemsSelected = !areAllItemsEmpty
    && selectableTypes.length === selectedTypes.length;

  // store copy of `items` so checkboxes will remain visible when they are disabled
  React.useEffect(() => {
    if (!isEmpty(items)) {
      setStoredItems(items);
    }
  });

  const getIsDisabledStatus = (
    disabled?: boolean
  ): boolean => (
    areAllItemsEmpty || Boolean(disabled)
  );

  const getClickableClasses = (disabled?: boolean) => (
    classnames('task-selector__clickable', {
      'task-selector__clickable--is-disabled': getIsDisabledStatus(disabled)
    })
  );

  const handleChange = (
    event: React.SyntheticEvent<HTMLElement>
  ): void => {
    const target = event.currentTarget;
    const identifier = target.dataset.identifier || '';
    const checked = !(target.dataset.checked === 'true');

    const updatedItems = items.map(item => {
      if (item.identifier === identifier || identifier === 'select-all') {
        return {
          ...item,
          isSelected: checked
        };
      }

      return item;
    });

    onChange(updatedItems, identifier);
  };

  const sortItems = (
    a: TaskSelectorItem,
    b: TaskSelectorItem
  ): number => (
    SORT_TEMPLATE.indexOf(a.identifier) - SORT_TEMPLATE.indexOf(b.identifier)
  );

  const renderIcon = (item: TaskSelectorItem) => (
    <div className="task-selector__icon">
      <SVGDynamicIcon
        name={item.icon}
        width={16}
        height={16}
        customColor="black"
      />
    </div>
  );

  const renderCheckbox = (
    identifier: string,
    selectable: boolean,
    isSelected: boolean
  ) => {
    const isChecked = (isSelected && selectedTypes.includes(identifier))
      || (identifier === 'select-all' && isSelected);

    return (
      <div
        key={`task-selector__checkbox--${identifier}`}
        className="task-selector__checkbox"
      >
        <InputCheckbox
          id={`task-selector-checkbox--${identifier}`}
          name="task-selector-checkbox"
          checked={isChecked}
          disabled={getIsDisabledStatus(!selectable)}
          data={{ identifier, checked: isSelected }}
          onChange={handleChange}
        />
      </div>
    );
  };

  const renderItem = (item: TaskSelectorItem) => (
    <div
      key={`task-selector__item--${item.identifier}`}
      className="task-selector__item"
    >
      {renderCheckbox(item.identifier, item.selectable, item.isSelected)}
      <div
        className={getClickableClasses(!item.selectable)}
        data-identifier={item.identifier}
        data-checked={item.isSelected}
        onClick={handleChange}
      >
        {
          withIcon && renderIcon(item)
        }
        <div className="task-selector__label">
          {item.label}
        </div>
      </div>
    </div>
  );

  const renderItemWithTooltip = (
    item: TaskSelectorItem
  ) => (
    <Popover
      key={`task-selector__item--${item.identifier}`}
      triggerComponent={renderItem(item)}
      classNamePopover="task-selector__popover"
    >
      {tooltipMsg}
    </Popover>
  );

  return (
    <div className="task-selector">
      <div className="task-selector__items">
        {
          withSelectAll && (
            <div
              key="task-selector__item--select-all"
              className="task-selector__item"
            >
              {renderCheckbox('select-all', true, areAllItemsSelected)}
              <div className={getClickableClasses()}>
                <div
                  className="task-selector__label"
                  data-identifier="select-all"
                  data-checked={areAllItemsSelected}
                  onClick={handleChange}
                >
                  {__t(`${MSG_KEY}.labels.select_all`)}
                </div>
              </div>
            </div>
          )
        }
        {
          (areAllItemsEmpty ? storedItems : items)
            .sort(sortItems).map(item => {
              if (!item.selectable) {
                return renderItemWithTooltip(item);
              }

              return renderItem(item);
            })
        }
      </div>
    </div>
  );
};

export default TaskSelector;
